<template>
  <footer class="w-100b bg-16151A">
    <Column class="w-100b py-100px box-border text-9b" x="c">
      <Row class="w-full max-w-1200px px-24px box-border gap-130px flex-wrap" x="sb" y="s">
        <Column class="w-184px shrink-0 gap-20px" y="s">
          <img src="@/image/home/logo-footer.webp" alt="BiFinance" class="w-174px block mb-44px">
          <Row class="flex-wrap w-184px" x="sb" y="c">
            <button
              v-for="item in socialLinks"
              :key="item.url"
              type="button"
              class="w-24px h-24px p-0 border-none bg-transparent text-9b cursor-pointer transition-colors duration-200 hover:text-while"
              :aria-label="item.label"
              @click="open(item.url)"
            >
              <IconSvg :name="item.icon" class="text-24px" />
            </button>
          </Row>
          <Row class="text-14px lh-22px text-9b">©{{ currentYear }} BiFinance.com</Row>
        </Column>

        <Row class="f-1 min-w-0" x="sb" y="s">
          <Column
            v-for="section in footerSections"
            :key="section.titleKey"
            :class="section.type === 'contact' ? 'w-220px gap-20px' : 'w-140px gap-20px'"
            y="s"
          >
            <Row class="text-20px lh-28px text-while weight-500 mb-14px">{{ $t(section.titleKey) }}</Row>

            <template v-if="section.type === 'contact'">
              <Column
                v-for="item in section.items"
                :key="item.key"
                class="gap-8px"
                y="s"
              >
                <Row class="text-16px lh-26px text-9b">{{ $t(item.labelKey) }}</Row>
                <a
                  :href="item.href"
                  class="text-16px lh-26px text-white underline break-all transition-colors duration-200 hover:text-while"
                >
                  {{ item.value }}
                </a>
              </Column>
            </template>

            <template v-else>
              <button
                v-for="item in section.items"
                :key="item.key"
                type="button"
                class="p-0 border-none bg-transparent text-16px lh-26px text-9b text-left cursor-pointer transition-colors duration-200 hover:text-while"
                @click="item.action()"
              >
                {{ item.labelKey ? $t(item.labelKey) : item.label }}
              </button>
            </template>
          </Column>
        </Row>
      </Row>
    </Column>
  </footer>
</template>

<script setup>
const userStore = useUserStore()
const configStore = useConfigStore()
const route = useRoute()

const currentYear = dayjs().year()
const isApiDocsVisible = $computed(() => Boolean(userStore.token && userStore?.info?.apiKeyStatus == 1))

const socialLinks = [
  {
    icon: 'icon-IconTelegram_Footer',
    label: 'Telegram Global',
    url: 'https://t.me/BiFinance_Global'
  },
  {
    icon: 'icon-IconTelegram_Footer',
    label: 'Telegram CN',
    url: 'https://t.me/BiFinance_Globalcn'
  },
  {
    icon: 'icon-IconFacebook_Footer',
    label: 'Facebook',
    url: 'https://www.facebook.com/BiFinanceExchange'
  },
  {
    icon: 'icon-IconTwitter_Footer',
    label: 'Twitter',
    url: 'https://twitter.com/BiFinanceE'
  },
  {
    icon: 'icon-IconMedium_Footer',
    label: 'Medium',
    url: 'https://medium.com/@BiFinancex'
  }
]

const footerSections = $computed(() => [
  {
    type: 'links',
    titleKey: 'footer.about',
    items: [
      {
        key: 'about-us',
        labelKey: 'footer.aboutUs',
        action: () => routerTo('/help/aboutUs')
      },
      {
        key: 'about-bft',
        label: 'About BFT',
        action: () => routerTo('/bft')
      },
      {
        key: 'investment-institution',
        labelKey: 'home.marjorShareholder',
        action: () => routerTo('/majroShareholder')
      },
      {
        key: 'bulletin',
        labelKey: 'footer.bulletin',
        action: () => toLink()
      },
      {
        key: 'brand-dynamics',
        labelKey: 'footer.brandDy',
        action: () => brandJump()
      },
      {
        key: 'law-enforcement',
        labelKey: 'footer.lawEnforcement',
        action: () => routerTo('/lawEnforcement')
      },
      {
        key: 'proof-of-reserves',
        labelKey: 'proofOfReserves.title',
        action: () => routerTo('/proofOfReserves')
      }
    ]
  },
  {
    type: 'links',
    titleKey: 'footer.service',
    items: [
      {
        key: 'user-agreement',
        labelKey: 'footer.userAgreement',
        action: () => routerTo('/help/content?type=userAgreement')
      },
      {
        key: 'fees',
        labelKey: 'footer.fees',
        action: () => routerTo('/help/fees')
      },
      {
        key: 'help-center',
        labelKey: 'footer.helpCenter',
        action: () => toLink()
      },
      {
        key: 'channel-verify',
        labelKey: 'footer.channelVerify',
        action: () => routerTo('/channelVerify')
      },
      {
        key: 'partner-recruitment',
        labelKey: 'agent.partnerRecruitment',
        action: () => routerTo('/agentApplication')
      },
      ...(isApiDocsVisible
        ? [{
          key: 'api-docs',
          label: 'APIs',
          action: () => routerTo('/docsApi')
        }]
        : [])
    ]
  },
  {
    type: 'links',
    titleKey: 'footer.support',
    items: [
      {
        key: 'privacy-policy',
        labelKey: 'footer.privacyPolicy_o',
        action: () => routerTo('/help/content?type=privacyPolicy_o')
      },
      {
        key: 'responsibility',
        labelKey: 'footer.responsibility_o',
        action: () => routerTo('/help/content?type=responsibility_o')
      },
      {
        key: 'kyc',
        labelKey: 'footer.kyc',
        action: () => routerTo('/help/content?type=kyc')
      }
    ]
  },
  {
    type: 'contact',
    titleKey: 'footer.contactUs',
    items: [
      {
        key: 'service-email',
        labelKey: 'footer.contactUs',
        href: 'mailto:service@bifinance.com',
        value: 'service@bifinance.com'
      },
      {
        key: 'listing-email',
        labelKey: 'footer.applyToList',
        href: 'mailto:listing@bifinance.com',
        value: 'listing@bifinance.com'
      }
    ]
  }
])

const open = (url) => {
  if (!/^http*/.test(url)) {
    url = 'http://' + url
  }
  window.open(url, '_blank')
}

const getHelpLocale = () => {
  const localeMap = {
    [LANGEN]: 'en-001',
    [LANGCN]: 'zh-cn',
    [LANGTW]: 'zh-tw',
    [LANGJA]: 'en-001',
    [LANGKO]: 'en-001'
  }
  const currentLocale = configStore.locale === 'hk' ? LANGTW : configStore.locale
  return localeMap[currentLocale] || localeMap[LANGEN]
}

const toLink = () => {
  open(`https://bifinance.zendesk.com/hc/${getHelpLocale()}`)
}

const brandJump = () => {
  const localePath = {
    [LANGEN]: '/sections/7124911573135-Brand-News',
    [LANGCN]: '/sections/7124911573135-%E5%93%81%E7%89%8C%E5%8A%A8%E6%80%81',
    [LANGTW]: '/categories/8777742546191',
    [LANGJA]: '/sections/7124911573135-Brand-News',
    [LANGKO]: '/sections/7124911573135-Brand-News'
  }
  const currentLocale = configStore.locale === 'hk' ? LANGTW : configStore.locale
  const path = localePath[currentLocale] || localePath[LANGEN]
  open(`https://bifinance.zendesk.com/hc/${getHelpLocale()}${path}`)
}

const routerTo = (path) => {
  if (route.query.winId) {
    winMes.postParent('footerRouteTo', path)
  } else {
    router.push(path)
  }
}
</script>
